import React, { useState } from 'react';
import { 
  Plus,
  Trash2,
  Edit3,
  ToggleLeft,
  ToggleRight,
  Target,
  CheckCircle,
  Shield,
  ShoppingBag,
  Info
} from 'lucide-react';
import { MotorbikeIcon } from '../Upload Doc Invoice/Icons';

const AddonsSettings = ({ 
  settings,
  handleSettingChange,
  currentCompany
}) => {
  
  // Addon management state
  const [showAddAddon, setShowAddAddon] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [newAddon, setNewAddon] = useState({
    name: '',
    category: 'Clothing',
    price: '0.00',
    pricingMethod: 'perDay'
  });

  // Available categories
  const addonCategories = [
    { value: 'Clothing', icon: '👕', color: 'blue' },
    { value: 'Safety', icon: '🦺', color: 'yellow' },
    { value: 'Equipment', icon: '🎒', color: 'green' },
    { value: 'Accessories', icon: '🧤', color: 'purple' },
    { value: 'Electronics', icon: '📱', color: 'cyan' },
    { value: 'Navigation', icon: '🗺️', color: 'indigo' },
    { value: 'Storage', icon: '🎒', color: 'pink' },
    { value: 'Other', icon: '📦', color: 'gray' }
  ];

  // Safe addon access - always returns array
  const getAddonsSafely = () => {
    return Array.isArray(settings.rentalAddons) ? settings.rentalAddons : [];
  };

  // Safe filter for enabled addons
  const getEnabledAddons = () => {
    const addons = getAddonsSafely();
    return addons.filter(addon => addon && addon.enabled === true);
  };

  // Group addons by category
  const getAddonsByCategory = () => {
    const addons = getAddonsSafely();
    const grouped = {};
    
    addons.forEach(addon => {
      if (!grouped[addon.category]) {
        grouped[addon.category] = [];
      }
      grouped[addon.category].push(addon);
    });
    
    return grouped;
  };

  // Addon management functions
  const addAddon = () => {
    if (!newAddon.name.trim()) {
      alert('Please enter addon name');
      return;
    }

    const currentAddons = getAddonsSafely();
    const newId = currentAddons.length > 0 ? Math.max(...currentAddons.map(a => a.id || 0)) + 1 : 1;
    
    const addon = {
      id: newId,
      name: newAddon.name.trim(),
      category: newAddon.category,
      enabled: false,
      price: newAddon.price,
      pricingMethod: newAddon.pricingMethod
    };

    handleSettingChange('rentalAddons', [...currentAddons, addon]);

    setNewAddon({ name: '', category: 'Clothing', price: '0.00', pricingMethod: 'perDay' });
    setShowAddAddon(false);
  };

  const removeAddon = (addonId) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) {
      return;
    }
    
    const currentAddons = getAddonsSafely();
    const updatedAddons = currentAddons.filter(a => a && a.id !== addonId);
    handleSettingChange('rentalAddons', updatedAddons);
  };

  const editAddon = (addon) => {
    setEditingAddon(addon);
    setNewAddon({
      name: addon.name,
      category: addon.category,
      price: addon.price,
      pricingMethod: addon.pricingMethod
    });
    setShowAddAddon(true);
  };

  const updateAddon = () => {
    if (!newAddon.name.trim()) {
      alert('Please enter addon name');
      return;
    }

    const currentAddons = getAddonsSafely();
    const updatedAddons = currentAddons.map(a => 
      a && a.id === editingAddon.id 
        ? {
            ...a,
            name: newAddon.name.trim(),
            category: newAddon.category,
            price: newAddon.price,
            pricingMethod: newAddon.pricingMethod
          }
        : a
    );

    handleSettingChange('rentalAddons', updatedAddons);

    setEditingAddon(null);
    setNewAddon({ name: '', category: 'Clothing', price: '0.00', pricingMethod: 'perDay' });
    setShowAddAddon(false);
  };

  const toggleAddon = (addonId) => {
    const currentAddons = getAddonsSafely();
    const updatedAddons = currentAddons.map(a => 
      a && a.id === addonId ? { ...a, enabled: !a.enabled } : a
    );
    handleSettingChange('rentalAddons', updatedAddons);
  };

  const updateAddonProperty = (addonId, property, value) => {
    const currentAddons = getAddonsSafely();
    const updatedAddons = currentAddons.map(a => 
      a && a.id === addonId ? { ...a, [property]: value } : a
    );
    handleSettingChange('rentalAddons', updatedAddons);
  };

  const cancelAddonForm = () => {
    setShowAddAddon(false);
    setEditingAddon(null);
    setNewAddon({ name: '', category: 'Clothing', price: '0.00', pricingMethod: 'perDay' });
  };

  const getCategoryColor = (category) => {
    const cat = addonCategories.find(c => c.value === category);
    return cat ? cat.color : 'gray';
  };

  const getCategoryIcon = (category) => {
    const cat = addonCategories.find(c => c.value === category);
    return cat ? cat.icon : '📦';
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center gap-3">
              <MotorbikeIcon className="w-6 h-6 text-orange-500" />
              Rental Add-ons
              <div className="ml-auto flex items-center text-orange-400 text-xs">
                <Target size={12} className="mr-1" />
                <span>Clothing & Equipment</span>
              </div>
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              Configure clothing and equipment add-ons for rentals
            </p>
          </div>
          <button
            onClick={() => setShowAddAddon(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
          >
            <Plus size={16} />
            <span>Add Custom</span>
          </button>
        </div>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 flex items-start gap-2">
          <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-blue-300 font-medium mb-1">Rental Add-ons System</p>
            <p className="text-blue-200">
              Enable add-ons to offer customers additional items. Set pricing per day or as a flat fee. Only enabled add-ons will be available during reservation.
            </p>
          </div>
        </div>

        {/* Add/Edit Addon Form */}
        {showAddAddon && (
          <div className="bg-gray-700/30 rounded-lg p-4 border border-orange-500/50">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Plus size={16} className="text-orange-400" />
                {editingAddon ? 'Edit Add-on' : 'Add Custom Add-on'}
              </h4>
              <button
                onClick={cancelAddonForm}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Add-on Name *
                </label>
                <input
                  type="text"
                  value={newAddon.name}
                  onChange={(e) => setNewAddon(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g., Safety Vest"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={newAddon.category}
                  onChange={(e) => setNewAddon(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  {addonCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.value}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Price (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newAddon.price}
                  onChange={(e) => setNewAddon(prev => ({ ...prev, price: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pricing Method
                </label>
                <select
                  value={newAddon.pricingMethod}
                  onChange={(e) => setNewAddon(prev => ({ ...prev, pricingMethod: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="perDay">Per Day</option>
                  <option value="flatFee">Flat Fee (One-time)</option>
                </select>
              </div>
            </div>

            {/* Preview */}
            {newAddon.name && newAddon.price && (
              <div className="mt-4 bg-gray-800/50 rounded-lg p-3 border border-gray-600">
                <p className="text-xs text-gray-400 mb-2">Preview:</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{newAddon.name}</p>
                    <p className="text-xs text-gray-400">{newAddon.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">${newAddon.price}</p>
                    <p className="text-xs text-gray-400">
                      {newAddon.pricingMethod === 'perDay' ? 'per day' : 'flat fee'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={editingAddon ? updateAddon : addAddon}
                className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
              >
                <CheckCircle size={16} />
                {editingAddon ? 'Update' : 'Add'} Add-on
              </button>
              <button
                onClick={cancelAddonForm}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Addons List */}
        <div>
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-400" />
            Configured Add-ons ({getAddonsSafely().length} total, {getEnabledAddons().length} enabled)
          </h4>
          
          {getAddonsSafely().length === 0 ? (
            <div className="bg-gray-700/30 rounded-lg p-8 border border-gray-600 text-center">
              <ShoppingBag size={48} className="mx-auto text-gray-500 mb-4" />
              <p className="text-gray-400 mb-2">No add-ons configured yet</p>
              <p className="text-gray-500 text-sm">Click "Add Custom" to create your first add-on</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getAddonsSafely().map((addon) => (
                <div 
                  key={addon.id} 
                  className={`bg-gray-700/30 rounded-lg p-4 border transition-all ${
                    addon.enabled 
                      ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' 
                      : 'border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        addon.enabled ? 'bg-orange-500/20' : 'bg-gray-600/50'
                      }`}>
                        <span className="text-xl">{getCategoryIcon(addon.category)}</span>
                      </div>
                      <div>
                        <h5 className="text-white font-medium flex items-center gap-2">
                          {addon.name}
                          {addon.enabled && (
                            <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full border border-green-700">
                              Active
                            </span>
                          )}
                        </h5>
                        <p className="text-gray-400 text-xs">{addon.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => editAddon(addon)}
                        className="p-1 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded hover:from-orange-600 hover:to-red-700 transition-colors"
                        title="Edit"
                      >
                        <Edit3 size={12} />
                      </button>
                      <button
                        onClick={() => removeAddon(addon.id)}
                        className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                      <button
                        onClick={() => toggleAddon(addon.id)}
                        className={`transition-colors ${
                          addon.enabled 
                            ? 'text-orange-400' 
                            : 'text-gray-500'
                        }`}
                        title={addon.enabled ? 'Disable' : 'Enable'}
                      >
                        {addon.enabled ? (
                          <ToggleRight size={24} className="text-orange-500" />
                        ) : (
                          <ToggleLeft size={24} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Price Configuration - Only shown when enabled */}
                  {addon.enabled && (
                    <div className="space-y-3 pt-3 border-t border-gray-600">
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">
                          Price (USD)
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-300 text-sm">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={addon.price}
                            onChange={(e) => updateAddonProperty(addon.id, 'price', e.target.value)}
                            className="flex-1 p-1 text-sm bg-gray-700 border border-gray-600 text-white rounded focus:ring-1 focus:ring-orange-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">
                          Pricing Method
                        </label>
                        <div className="space-y-1">
                          <label className="flex items-center text-xs">
                            <input
                              type="radio"
                              name={`pricing-${addon.id}`}
                              value="perDay"
                              checked={addon.pricingMethod === 'perDay'}
                              onChange={(e) => updateAddonProperty(addon.id, 'pricingMethod', e.target.value)}
                              className="mr-2 text-orange-500"
                            />
                            <span className="text-white">Per Day (multiplied by rental days)</span>
                          </label>
                          
                          <label className="flex items-center text-xs">
                            <input
                              type="radio"
                              name={`pricing-${addon.id}`}
                              value="flatFee"
                              checked={addon.pricingMethod === 'flatFee'}
                              onChange={(e) => updateAddonProperty(addon.id, 'pricingMethod', e.target.value)}
                              className="mr-2 text-orange-500"
                            />
                            <span className="text-white">Flat Fee (one-time charge)</span>
                          </label>
                        </div>
                      </div>

                      <div className="bg-gray-800/50 rounded p-2 border border-gray-600">
                        <div className="text-xs text-gray-400">Customer sees:</div>
                        <div className="text-white text-sm font-medium">
                          ${addon.price} {addon.pricingMethod === 'perDay' ? '/ day' : 'flat'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary by Category */}
        {getAddonsSafely().length > 0 && (
          <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <ShoppingBag size={16} className="text-orange-400" />
              Add-ons by Category
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(getAddonsByCategory()).map(([category, addons]) => (
                <div key={category} className="bg-gray-800/50 rounded p-3 border border-gray-600">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{getCategoryIcon(category)}</span>
                    <span className="text-sm font-medium text-gray-300">{category}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    {addons.length} total • {addons.filter(a => a.enabled).length} enabled
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Configuration Summary */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-orange-400" />
            Add-ons Configuration Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-gray-400">Total Add-ons:</span>
              <span className="text-white ml-2 font-medium">
                {getAddonsSafely().length}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Enabled:</span>
              <span className={`ml-2 font-medium ${
                getEnabledAddons().length > 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {getEnabledAddons().length}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Categories:</span>
              <span className="text-white ml-2 font-medium">
                {Object.keys(getAddonsByCategory()).length}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Per Day:</span>
              <span className="text-blue-400 ml-2 font-medium">
                {getEnabledAddons().filter(a => a.pricingMethod === 'perDay').length}
              </span>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-xs text-gray-500 bg-gray-700/30 rounded p-3 border border-gray-600">
          <p className="font-medium text-gray-400 mb-1">💡 Tips:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Enable add-ons to make them available during reservation creation</li>
            <li>Per Day pricing multiplies by the number of rental days</li>
            <li>Flat Fee pricing charges once regardless of rental duration</li>
            <li>Use categories to organize add-ons for easier management</li>
            <li>Disabled add-ons are hidden from customers but remain in your system</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AddonsSettings;